import type { TemplateType } from '../constants/contentAuthoringEnums';
import { PENDO_EVENTS, trackPendoEvent } from './pendo';

export const AUTHORING_PENDO_PRODUCT_AREA = 'content_authoring';

export type AuthoringPendoEntity = 'template' | 'course' | 'translation';

/** Intent (footer/list/menu) vs completed API/job. */
export type AuthoringPendoAction = 'click' | 'outcome';

/** Where the user started the action (for funnel drop-off). */
export type AuthoringPendoEntryPoint = 'list' | 'editor' | 'row_menu' | 'translations_hub' | 'wizard';

export type AuthoringPendoImageSource = 'upload' | 'media_collection' | 'generate';

export type AuthoringPendoOutcome = 'success' | 'failed';

export type AuthoringScormVersion = '1.2' | '2004-3rd' | '2004-4th';

export type AuthoringExportPackaging = 'single' | 'separate';

/**
 * Opaque entity correlation for per-object funnels (no names / PII).
 * User/org context comes from Pendo visitor + account at init.
 */
export type AuthoringPendoEntityCorrelation = {
  organizationId?: string;
  templateId?: string;
  courseId?: string;
  parentCourseId?: string;
};

export function authoringEntityCorrelation(
  ids: AuthoringPendoEntityCorrelation,
): Record<string, string> {
  const result: Record<string, string> = {};
  if (ids.organizationId) result.organizationId = ids.organizationId;
  if (ids.templateId) result.templateId = ids.templateId;
  if (ids.courseId) result.courseId = ids.courseId;
  if (ids.parentCourseId) result.parentCourseId = ids.parentCourseId;
  return result;
}

/**
 * Core dimensions on every Content Authoring track event (CEE-1211).
 * Plus `environment` (auto) and Pendo visitor/account (init).
 */
export type AuthoringPendoBaseMetadata = {
  productArea: typeof AUTHORING_PENDO_PRODUCT_AREA;
  entityType: AuthoringPendoEntity;
  action: AuthoringPendoAction;
};

export type AuthoringPendoClickMetadata = AuthoringPendoBaseMetadata & {
  action: 'click';
  entryPoint: AuthoringPendoEntryPoint;
};

export type AuthoringPendoOutcomeMetadata = AuthoringPendoBaseMetadata & {
  action: 'outcome';
  outcome: AuthoringPendoOutcome;
};

export function authoringClickMetadata(
  entityType: AuthoringPendoEntity,
  entryPoint: AuthoringPendoEntryPoint,
  extra?: Record<string, unknown>,
): AuthoringPendoClickMetadata & Record<string, unknown> {
  return {
    productArea: AUTHORING_PENDO_PRODUCT_AREA,
    entityType,
    action: 'click',
    entryPoint,
    ...extra,
  };
}

export function authoringOutcomeMetadata(
  entityType: AuthoringPendoEntity,
  outcome: AuthoringPendoOutcome,
  extra?: Record<string, unknown>,
): AuthoringPendoOutcomeMetadata & Record<string, unknown> {
  return {
    productArea: AUTHORING_PENDO_PRODUCT_AREA,
    entityType,
    action: 'outcome',
    outcome,
    ...extra,
  };
}

/** Merges standard authoring metadata and forwards to Pendo. */
export function trackAuthoringPendoEvent(
  eventName: string,
  metadata: Record<string, unknown>,
): void {
  trackPendoEvent(eventName, metadata);
}

export function trackAuthoringTemplateOutcome(
  eventName: string,
  templateType: TemplateType,
  outcome: AuthoringPendoOutcome,
  extra?: Record<string, unknown>,
): void {
  trackAuthoringPendoEvent(
    eventName,
    authoringOutcomeMetadata('template', outcome, { templateType, ...extra }),
  );
}

export function trackAuthoringCourseOutcome(
  eventName: string,
  outcome: AuthoringPendoOutcome,
  extra?: Record<string, unknown>,
): void {
  trackAuthoringPendoEvent(eventName, authoringOutcomeMetadata('course', outcome, extra));
}

export function trackAuthoringTranslationOutcome(
  eventName: string,
  outcome: AuthoringPendoOutcome,
  extra?: Record<string, unknown>,
): void {
  trackAuthoringPendoEvent(eventName, authoringOutcomeMetadata('translation', outcome, extra));
}

/** @deprecated Use trackAuthoringTemplateOutcome */
export function trackAuthoringTemplateLifecycle(
  eventName: string,
  templateType: TemplateType,
  extra?: Record<string, unknown>,
): void {
  trackAuthoringTemplateOutcome(eventName, templateType, 'success', extra);
}

/** @deprecated Use trackAuthoringCourseOutcome */
export function trackAuthoringCourseLifecycle(
  eventName: string,
  extra?: Record<string, unknown>,
): void {
  trackAuthoringCourseOutcome(eventName, 'success', extra);
}

export { PENDO_EVENTS };
